import { PaymentMethod } from '@/models/PaymentMethod';
import { Amount } from '@/models/Price';

export type PlanCode = 'free' | 'paid';
export type SubscriptionStatus = 'none' | 'active' | 'canceled' | 'past_due' | 'paused';
export type PaymentStatus = 'ok' | 'action_required';
export type BillingCycle = 'monthly';

export const ALL_PLAN_CODES: PlanCode[] = ['free', 'paid'];

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  none: 'None',
  active: 'Active',
  canceled: 'Canceled',
  past_due: 'Past due',
  paused: 'Paused',
};

export interface BillingInfo {
  plan: Plan;
  subscription: Subscription;
  currencyCode: string;
  billingDetailsReadOnly: boolean;
  billingDetails?: BillingDetails;
  defaultPaymentMethod?: PaymentMethod;
}

export interface BillingDetails {
  companyName?: string | null;
  billingEmail?: string | null;
  taxId?: string | null;
  address?: BillingAddress | null;
}

export interface BillingAddress {
  id?: string;
  building: string;
  street1: string;
  street2: string;
  postalCode: string;
  region: string;
  city: string;
  countryCode: string;
}

export function isBillingAddressComplete(address?: BillingAddress | null): boolean {
  if (!address) {
    return false;
  }

  return [address.street1, address.postalCode, address.city, address.countryCode].every(
    (value) => value.trim().length > 0,
  );
}

export interface Plan {
  code: PlanCode;
  displayName?: string | null;
  description?: string | null;
}

export interface Subscription {
  status: SubscriptionStatus;
  paymentStatus?: PaymentStatus | null;
  billingCycle?: BillingCycle | null;
  startedAt?: Date | null;
  isActive?: boolean;
  currentPeriod?: {
    start: Date;
    end: Date;
  };
  nextBillingAt?: Date | null;
  cancelAtPeriodEnd?: boolean;
}

export interface BillingCustomerTarget {
  storeId?: string;
  supplierId?: string;
  retailerId?: string;
}

export type BillingOwnerType = 'store' | 'supplier' | 'retailer';

export interface BillingPricingProduct {
  product: string;
  isActive: boolean;
  vatRate: number;
  unitPrice: Amount;
  vatAmount: Amount;
  totalAmount: Amount;
}

export interface BillingPricingOption {
  countryCode: string;
  currencyCode: string;
  products: BillingPricingProduct[];
}

export interface BillingPricingPlan {
  planCode: Exclude<PlanCode, 'free'>;
  options: BillingPricingOption[];
}

export interface BillingPricing {
  ownerType: BillingOwnerType;
  plans: BillingPricingPlan[];
}

interface BaseSubscriptionUpdate {
  customer?: BillingCustomerTarget;
  startedAt?: Date | null;
}

export type FreeSubscriptionUpdate = BaseSubscriptionUpdate & {
  plan: 'free';
  cancelAtPeriodEnd?: boolean;
};

export type PaidSubscriptionUpdate = BaseSubscriptionUpdate & {
  plan: 'paid';
  billingCycle: BillingCycle;
  cancelAtPeriodEnd?: boolean;
};

export type SubscriptionUpdate = FreeSubscriptionUpdate | PaidSubscriptionUpdate;

export function formatSubscriptionStatus(status?: SubscriptionStatus | null): string {
  if (!status) {
    return 'Not available';
  }

  return SUBSCRIPTION_STATUS_LABELS[status];
}
